import anytimeConsultationApi from "../../api/anytime_consultations";
import { buildAnsweredSearchRequestQuery } from "../../features/anytime-consultation-answered-search";
import * as mutationTypes from "../mutation-types";

const state = {
  anytimeConsultations: [],
  anytimeConsultation: {},
  anytimeConsultationAnswer: {},
  anytimeConsultationKartes: [],
  anytimeConsultationSiblingKartes: [],
  anytimeConsultationReferenceInfo: [],
  anytimeConsultationSearchKeys: null,
  anytimeConsultationFilteredFlag: true,
  elasticsearchErrorDetail: "",
  popupMessage: "",
  totalCount: 0,
  excludedCount: 0,
  hasMore: false,
  offset: 0,
  lastSearchParams: null
};

const getters = {
  anytimeConsultations: state => state.anytimeConsultations,
  anytimeConsultation: state => state.anytimeConsultation,
  anytimeConsultationAnswer: state => state.anytimeConsultationAnswer,
  anytimeConsultationKartes: state => state.anytimeConsultationKartes,
  anytimeConsultationSiblingKartes: state => state.anytimeConsultationSiblingKartes,
  anytimeConsultationReferenceInfo: state => state.anytimeConsultationReferenceInfo,
  anytimeConsultationSearchKeys: state => state.anytimeConsultationSearchKeys,
  anytimeConsultationFilteredFlag: state => state.anytimeConsultationFilteredFlag,
  elasticsearchErrorDetail: state => state.elasticsearchErrorDetail,
  popupMessage: state => state.popupMessage,
  totalCount: state => state.totalCount,
  excludedCount: state => state.excludedCount,
  hasMore: state => state.hasMore,
  offset: state => state.offset,
  lastSearchParams: state => state.lastSearchParams
};

// anytimeConsultations は一覧・類似相談検索・回答済み検索で共有しているため、
// タブ切替や画面遷移で取得が並行した場合に、古いリクエストのレスポンスが新しい内容を上書きしないよう
// 世代IDで判定する。どの処理が最後に取得を始めたか（source）も保持し、
// 検索タブのクリア処理が一覧画面の内容を消さないようにする
const CONSULTATIONS_REQUEST_SOURCES = Object.freeze({
  LIST: "list",
  SIMILAR_SEARCH: "similarSearch",
  ANSWERED_SEARCH: "answeredSearch",
});

let latestConsultationsRequestId = 0;
let latestConsultationsRequestSource = null;

const startConsultationsRequest = (source) => {
  latestConsultationsRequestId += 1;
  latestConsultationsRequestSource = source;
  return latestConsultationsRequestId;
};

const isLatestConsultationsRequest = (requestId) => requestId === latestConsultationsRequestId;

// isSearching は類似相談検索・回答済み検索で共有しており、検索は実行中でも再実行できる。
// 古い検索の完了で新しい検索のローディングが消えないよう、最後に開始した検索だけが解除できるようにする。
// 一覧取得では isSearching を扱わないため、上の世代IDとは分けて管理する
// （共有すると、検索中に一覧取得が始まった場合に isSearching が true のまま残る）
let latestSearchingRequestId = 0;

const startSearching = (commit) => {
  latestSearchingRequestId += 1;
  commit(mutationTypes.ASSIGN_IS_SEARCHING, true);
  return latestSearchingRequestId;
};

const finishSearching = (commit, searchingRequestId) => {
  if (searchingRequestId !== latestSearchingRequestId) return;
  commit(mutationTypes.ASSIGN_IS_SEARCHING, false);
};

// 一覧取得系アクションの共通処理。最新リクエストのレスポンスのみを反映し、
// 反映有無をresolveの値として呼び出し元へ返す（APIエラー時も false）
const fetchConsultationsList = (commit, params, fetchFn) => {
  const requestId = startConsultationsRequest(CONSULTATIONS_REQUEST_SOURCES.LIST);
  // APIエラーの場合に前の情報が残らないようにする
  commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS, { anytimeConsultations: [] });

  return new Promise((resolve) => {
    fetchFn(anytimeConsultations => {
      if (!isLatestConsultationsRequest(requestId)) {
        resolve(false);
        return;
      }

      commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS, { anytimeConsultations });
      resolve(true);
    }, params, () => {
      resolve(false);
    });
  });
};

const actions = {
  fetchAnytimeConsultations({ commit }, params) {
    return fetchConsultationsList(commit, params, anytimeConsultationApi.fetchAnytimeConsultations);
  },

  fetchAssignedAnytimeConsultations({ commit }, params) {
    return fetchConsultationsList(commit, params, anytimeConsultationApi.fetchAssignedAnytimeConsultations);
  },

  fetchCompletedAnytimeConsultations({ commit }, params) {
    return fetchConsultationsList(commit, params, anytimeConsultationApi.fetchCompletedAnytimeConsultations);
  },

  // anytimeConsultations は画面遷移やログアウトをまたいで残るため、一覧画面の表示開始時やログアウト時に
  // 前回の一覧（別タブ・別アカウントの内容を含む）を消す。実行中の取得・検索の応答も反映されないよう世代を進める
  resetAnytimeConsultationsList({ commit }) {
    startConsultationsRequest(CONSULTATIONS_REQUEST_SOURCES.LIST);
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS, { anytimeConsultations: [] });
  },

  answeredSearchAnytimeConsultations({ commit }, params) {
    const requestId = startConsultationsRequest(CONSULTATIONS_REQUEST_SOURCES.ANSWERED_SEARCH);
    const searchingRequestId = startSearching(commit);
    // 新規検索時はオフセットを0にして状態をリセット
    commit(mutationTypes.RESET_ANYTIME_CONSULTATIONS);

    const request = {
      query: buildAnsweredSearchRequestQuery(params),
      offset: 0,
    };

    return anytimeConsultationApi.answeredSearchAnytimeConsultations(request)
      .then(response => {
        if (!isLatestConsultationsRequest(requestId)) return;

        const anytimeConsultations = response.data || [];
        const totalCount = response.total_count || 0;
        const excludedCount = response.excluded_count || 0;
        const hasMore = response.has_more || false;
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS, { anytimeConsultations, totalCount, excludedCount, hasMore });
        // 検索パラメータを保存（追加読み込み時に使用）
        commit(mutationTypes.ASSIGN_SEARCH_PARAMS, { params, offset: anytimeConsultations.length });
      })
      .finally(() => {
        finishSearching(commit, searchingRequestId);
      });
  },

  loadMoreAnytimeConsultations({ commit, state }) {
    const requestId = startConsultationsRequest(CONSULTATIONS_REQUEST_SOURCES.ANSWERED_SEARCH);
    const searchingRequestId = startSearching(commit);
    const params = { ...state.lastSearchParams };
    const request = {
      query: buildAnsweredSearchRequestQuery(params),
      offset: state.offset,
    };

    return anytimeConsultationApi.answeredSearchAnytimeConsultations(request)
      .then(response => {
        if (!isLatestConsultationsRequest(requestId)) return;

        const anytimeConsultations = response.data || [];
        const hasMore = response.has_more || false;
        const totalCount = response.total_count || 0;
        const excludedCount = response.excluded_count || 0;
        commit(mutationTypes.APPEND_ANYTIME_CONSULTATIONS, { anytimeConsultations, hasMore, totalCount, excludedCount });
        commit(mutationTypes.ASSIGN_SEARCH_PARAMS, { params: state.lastSearchParams, offset: state.offset + anytimeConsultations.length });
      })
      .finally(() => {
        finishSearching(commit, searchingRequestId);
      });
  },

  similarSearchAnytimeConsultations({ commit }, params) {
    const requestId = startConsultationsRequest(CONSULTATIONS_REQUEST_SOURCES.SIMILAR_SEARCH);
    return new Promise((resolve, reject) => {
      const searchingRequestId = startSearching(commit);
      anytimeConsultationApi.similarSearchAnytimeConsultations(params,
        (anytimeConsultations) => {
          finishSearching(commit, searchingRequestId);
          // 検索中に一覧画面へ戻った場合などに、一覧の内容を検索結果で上書きしない
          if (!isLatestConsultationsRequest(requestId)) {
            resolve();
            return;
          }
          commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS, { anytimeConsultations });
          const elasticsearchErrorDetail = "";
          commit(mutationTypes.ASSIGN_ELASTICSEARCH_ERROR_DETAIL, { elasticsearchErrorDetail });
          resolve();
        },
        (error) => {
          finishSearching(commit, searchingRequestId);
          if (isLatestConsultationsRequest(requestId)) {
            const elasticsearchErrorDetail = error.response.data["message"];
            commit(mutationTypes.ASSIGN_ELASTICSEARCH_ERROR_DETAIL, { elasticsearchErrorDetail });
          }
          reject();
        }
      );
    });
  },

  bulkExcludeAnytimeConsultations(_ctx, params) {
    return anytimeConsultationApi.bulkExcludeAnytimeConsultations(params);
  },

  bulkUnexcludeAnytimeConsultations(_ctx, params) {
    return anytimeConsultationApi.bulkUnexcludeAnytimeConsultations(params);
  },

  findAnytimeConsultation({ commit }, params) {
    return new Promise((resolve) => {
      anytimeConsultationApi.findAnytimeConsultation(anytimeConsultation => {
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION, { anytimeConsultation });
        resolve();
      }, params);
    });
  },

  findAnytimeConsultationAnswer({ commit }, params) {
    return new Promise((resolve) => {
      anytimeConsultationApi.findAnytimeConsultationAnswer(anytimeConsultationAnswer => {
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
        resolve();
      }, params);
    });
  },

  saveAnytimeConsultationAnswerDraft({ commit }, params) {
    return new Promise((resolve, reject) => {
      anytimeConsultationApi.saveAnytimeConsultationAnswerDraft(params,
        // success
        anytimeConsultationAnswer => {
          commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
          resolve();
        },
        error => {
          const popupMessage = error.response.data["message"];
          if (popupMessage) {
            commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER_POPUP_MESSAGE, { popupMessage });
          } else {
            console.error(error);
          }
          reject();
        }
      );
    });
  },

  completeAnytimeConsultationAnswer({ commit }, params) {
    return new Promise((resolve, reject) => {
      anytimeConsultationApi.completeAnytimeConsultationAnswer(params,
        anytimeConsultationAnswer => {
          commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
          resolve();
        },
        error => {
          const popupMessage = error.response.data["message"];
          if (popupMessage) {
            commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER_POPUP_MESSAGE, { popupMessage });
          } else {
            console.error(error);
          }
          reject();
        }
      );
    });
  },

  cancelAnytimeConsultationAnswer({ commit }, params) {
    return new Promise((resolve, reject) => {
      anytimeConsultationApi.cancelAnytimeConsultationAnswer(
        params,
        anytimeConsultationAnswer => {
          commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
          resolve();
        },
        error => {
          const popupMessage = error.response.data["message"];
          if (popupMessage) {
            commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER_POPUP_MESSAGE, { popupMessage });
          } else {
            console.error(error);
          }
          reject();
        });
    });
  },

  fetchAnytimeConsultationKartes({ commit }, params) {
    return new Promise((resolve) => {
      anytimeConsultationApi.fetchAnytimeConsultationKartes(anytimeConsultationKartes => {
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_KARTES, { anytimeConsultationKartes });
        resolve();
      }, params);
    });
  },

  fetchAnytimeConsultationSiblingKartes({ commit }, params) {
    return new Promise((resolve) => {
      anytimeConsultationApi.fetchAnytimeConsultationSiblingKartes(anytimeConsultationSiblingKartes => {
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_SIBLING_KARTES, { anytimeConsultationSiblingKartes });
        resolve();
      }, params);
    });
  },

  findAnytimeConsultationReferenceInfo({ commit }, params) {
    return new Promise((resolve) => {
      anytimeConsultationApi.findAnytimeConsultationReferenceInfo(anytimeConsultationReferenceInfo => {
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_REFERENCE_INFO, { anytimeConsultationReferenceInfo });
        resolve();
      }, params);
    });
  },

  assignAnytimeConsultationSearchKeys({ commit }, searchKeys) {
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_SEARCH_KEYS, { searchKeys });
  },

  assignFilteredFlag({ commit }, filtered) {
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_FILTERED_FLAG, { filtered });
  },

  clearAnytimeConsultationObjects({ commit }) {
    let anytimeConsultation = {};
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION, { anytimeConsultation });
    let anytimeConsultationAnswer = {};
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
    let anytimeConsultationReferenceInfo = [];
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_REFERENCE_INFO, { anytimeConsultationReferenceInfo });
  },

  clearAnytimeConsultationSearchTabObjects({ commit }) {
    // 詳細画面（検索タブ）のアンマウントは一覧画面の取得開始より後に起こり得るため、
    // 一覧が取得を始めている場合は一覧の内容を消さない
    if (latestConsultationsRequestSource !== CONSULTATIONS_REQUEST_SOURCES.LIST) {
      // 実行中の類似相談検索のレスポンスもクリア後に反映されないよう世代を進める
      startConsultationsRequest(CONSULTATIONS_REQUEST_SOURCES.SIMILAR_SEARCH);
      let anytimeConsultations = [];
      commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS, { anytimeConsultations });
    }
    let elasticsearchErrorDetail = "";
    commit(mutationTypes.ASSIGN_ELASTICSEARCH_ERROR_DETAIL, { elasticsearchErrorDetail });
  },

  clearAnytimeConsultationKartes({ commit }) {
    let anytimeConsultationKartes = [];
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_KARTES, { anytimeConsultationKartes });
  },

  clearAnytimeConsultationSiblingKartes({ commit }) {
    let anytimeConsultationSiblingKartes = [];
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_SIBLING_KARTES, { anytimeConsultationSiblingKartes });
  },

  deleteAnytimeConsultationAnswer({ commit }, params) {
    return new Promise((resolve, reject) => {
      anytimeConsultationApi.deleteAnytimeConsultationAnswer(
        params,
        anytimeConsultationAnswer => {
          commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
          resolve();
        },
        error => {
          const popupMessage = error.response.data["message"];
          if (popupMessage) {
            commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER_POPUP_MESSAGE, { popupMessage });
          } else {
            console.error(error);
          }
          // ステータスが変更されている場合があるのでデータを再読込する
          anytimeConsultationApi.findAnytimeConsultationAnswer(anytimeConsultationAnswer => {
            commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER, { anytimeConsultationAnswer });
          }, params);
          reject();
        });
    });
  },
};


const mutations = {
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATIONS](state, { anytimeConsultations, totalCount, excludedCount, hasMore }) {
    state.anytimeConsultations = anytimeConsultations;
    if (totalCount !== undefined) state.totalCount = totalCount;
    if (excludedCount !== undefined) state.excludedCount = excludedCount;
    if (hasMore !== undefined) state.hasMore = hasMore;
  },

  [mutationTypes.APPEND_ANYTIME_CONSULTATIONS](state, { anytimeConsultations, hasMore, totalCount, excludedCount }) {
    state.anytimeConsultations = [...state.anytimeConsultations, ...anytimeConsultations];
    if (hasMore !== undefined) state.hasMore = hasMore;
    if (totalCount !== undefined) state.totalCount = totalCount;
    if (excludedCount !== undefined) state.excludedCount = excludedCount;
  },

  [mutationTypes.RESET_ANYTIME_CONSULTATIONS](state) {
    state.anytimeConsultations = [];
    state.totalCount = 0;
    state.excludedCount = 0;
    state.hasMore = false;
    state.offset = 0;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION](state, { anytimeConsultation }) {
    state.anytimeConsultation = anytimeConsultation;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER](state, { anytimeConsultationAnswer }) {
    state.anytimeConsultationAnswer = anytimeConsultationAnswer;
  },

  [mutationTypes.SET_ANYTIME_CONSULTATION_ANSWER_CONTENT](state, { content }) {
    state.anytimeConsultationAnswer = {
      ...state.anytimeConsultationAnswer,
      content,
    };
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_KARTES](state, { anytimeConsultationKartes }) {
    state.anytimeConsultationKartes = anytimeConsultationKartes;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_SIBLING_KARTES](state, { anytimeConsultationSiblingKartes }) {
    state.anytimeConsultationSiblingKartes = anytimeConsultationSiblingKartes;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_REFERENCE_INFO](state, { anytimeConsultationReferenceInfo }) {
    state.anytimeConsultationReferenceInfo = anytimeConsultationReferenceInfo;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_SEARCH_KEYS](state, { searchKeys }) {
    state.anytimeConsultationSearchKeys = searchKeys;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_FILTERED_FLAG](state, { filtered }) {
    state.anytimeConsultationFilteredFlag = filtered;
  },

  [mutationTypes.ASSIGN_ELASTICSEARCH_ERROR_DETAIL](state, { elasticsearchErrorDetail }) {
    state.elasticsearchErrorDetail = elasticsearchErrorDetail;
  },

  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_ANSWER_POPUP_MESSAGE](state, { popupMessage }) {
    state.popupMessage = popupMessage;
  },

  [mutationTypes.ASSIGN_SEARCH_PARAMS](state, { params, offset }) {
    state.lastSearchParams = params;
    state.offset = offset;
  }
};


export default {
  state,
  getters,
  actions,
  mutations
};
